// ABOUTME: Defines feature flag configurations, keys, and their supported query parameters.
// ABOUTME: Provides type-safe contracts for global and plant-scoped feature checks.
export type ConfigurationName = 'suite' | 'hproduce-common'

export enum FeatureFlagConfig {
  Common = 'hproduce-common',
  Suite = 'suite'
}

export type FeatureFlag = {
  key: FeatureFlagKey
  value: boolean
}

export type ExactParams<
  C extends keyof FeatureFlagParams,
  K extends keyof FeatureFlagParams[C]
> = FeatureFlagParams[C][K] extends object ? FeatureFlagParams[C][K] : never

export enum FeatureFlagKey {
  DailyReportingJanus = 'DailyReportingJanus',
  InputRangesAndDecimals = 'inputrangesanddecimals',
  MonthlyReportingJanus = 'MonthlyReportingJanus',
  PlantAdministration = 'plantadministration',
  PSSJanus = 'PSSJanus',
  PSSStandardCapacities = 'pssstandardcapacities',
  ReportingReminders = 'reportingreminders',
  SLATracking = 'slatracking',
  Validation = 'validation',
  ValidationAndChecks = 'validationandchecks',
  VersionManagement = 'VersionManagement',
  HideWorkOrderNotification = 'hideWorkOrderNotification',
  ExtendedEquipmentIds = 'extendedEquipmentIds',
  AutocompleteProcessStage = 'autocompleteProcessStage',
  Checklists = 'checklists',
  SubmitStoppage = 'submitStoppage',
  UseHproduceDomain = 'useHproduceDomain',
  FunctionalLocationOptional = 'functionalLocationOptional',
  SapMaintenanceNotification = 'sapMaintenanceNotification',
  InforMaintenanceNotification = 'inforMaintenanceNotification',
  EnableSplitStoppage = 'enableSplitStoppage',
  MovieAttachment = 'movieAttachment',
  TermsAndConditions = 'termsAndConditions',
  LabQualityResultPage = 'labQualityResultPage',
  Notifications = 'notifications',
  MainEquipmentPrefill = 'mainEquipmentPrefill',
  MainEquipmentList = 'mainEquipmentList',
  AllProcessStagesSummary = 'allProcessStagesSummary',
  ProductionVolumes = 'productionVolumes',
  ExpertSystem = 'expertSystem',
  SiloFillLevels = 'siloFillLevels',
  StoppedMainEquipment = 'stoppedMainEquipment',
  KpiAggregationTypeOption = 'kpiAggregationTypeOption',
  ChecklistsKilnCheckSheet = 'checklistsKilnCheckSheet',
  StoppageOverlapValidation = 'stoppageOverlapValidation',
  StoppageAnalysisGraphEditMode = 'stoppageAnalysisGraphEditMode',
  RiskSurvey = 'riskSurvey',
  AllowManualUpmCreation = 'allowManualUpmCreation',
  Stock = 'stock_screen',
  PriceForecast = 'price_forecast_screen',
  DemandForecast = 'demand_forecast_screen',
  Planning = 'planning_screen',
  LegacyElectricityPurchasing = 'electricity_purchasing',
  ControlOperatorScreen = 'control_room_operator_view',
  LegacyPlanningComments = 'planning_comments',
  LegacyOperatorViewExactElectricityPrices = 'operator_view_exact_electricity_prices',
  LegacyOperatorViewColorCodedElectricityPrices = 'operator_view_color_coded_electricity_prices',
  LegacyShutdownAvailability = 'shutdown_availability',
  LegacyCostAvoidance = 'cost_avoidance',
  UpmAssetsAndStorages = 'upm_assets_and_storages',
  NewStandardOperationTimes = 'newStandardOperationTimes',
  JanusShowPssTab = 'janus_show_pss_tab',
  EnableOptimizedAssets = 'enable_optimized_assets',
  SuiteMenuCustomization = 'suiteMenuCustomization',
  ElectricityPriceForecast = 'electricityPriceForecast',
  SalesForecast = 'salesForecast',
  OperatorView = 'operatorView',
  StorageLevels = 'storageLevels',
  ProductionPlanning = 'productionPlanning',
  Optimizer = 'optimizer',
  CostAvoidance = 'costAvoidance',
  ElectricityPurchasing = 'electricityPurchasing',
  PlanningComments = 'planningComments',
  OperatorViewExactElectricityPrices = 'operatorViewExactElectricityPrices',
  OperatorViewColorCodedElectricityPrices = 'operatorViewColorCodedElectricityPrices',
  ShutdownAvailability = 'shutdownAvailability',
  RawMaterialsPlanning = 'rawMaterialsPlanning',
  ThreeShippingTypes = 'threeShippingTypes',
  OptimizerStatusDisplay = 'optimizerStatusDisplay',
  OptimizerPlanAnalysis = 'optimizerPlanAnalysis'
}

type SuiteFeatureFlags =
  | FeatureFlagKey.DailyReportingJanus
  | FeatureFlagKey.MonthlyReportingJanus
  | FeatureFlagKey.VersionManagement
  | FeatureFlagKey.InputRangesAndDecimals
  | FeatureFlagKey.PlantAdministration
  | FeatureFlagKey.PSSJanus
  | FeatureFlagKey.PSSStandardCapacities
  | FeatureFlagKey.ReportingReminders
  | FeatureFlagKey.SLATracking
  | FeatureFlagKey.Validation
  | FeatureFlagKey.ValidationAndChecks
  | FeatureFlagKey.RiskSurvey
  | FeatureFlagKey.ElectricityPriceForecast
  | FeatureFlagKey.SalesForecast
  | FeatureFlagKey.OperatorView
  | FeatureFlagKey.StorageLevels
  | FeatureFlagKey.ProductionPlanning
  | FeatureFlagKey.Optimizer

type HProduceCommonFeatureFlags =
  | FeatureFlagKey.HideWorkOrderNotification
  | FeatureFlagKey.ExtendedEquipmentIds
  | FeatureFlagKey.AutocompleteProcessStage
  | FeatureFlagKey.Checklists
  | FeatureFlagKey.SubmitStoppage
  | FeatureFlagKey.FunctionalLocationOptional
  | FeatureFlagKey.SapMaintenanceNotification
  | FeatureFlagKey.InforMaintenanceNotification
  | FeatureFlagKey.EnableSplitStoppage
  | FeatureFlagKey.MovieAttachment
  | FeatureFlagKey.TermsAndConditions
  | FeatureFlagKey.LabQualityResultPage
  | FeatureFlagKey.Notifications
  | FeatureFlagKey.MainEquipmentPrefill
  | FeatureFlagKey.MainEquipmentList
  | FeatureFlagKey.AllProcessStagesSummary
  | FeatureFlagKey.ProductionVolumes
  | FeatureFlagKey.ExpertSystem
  | FeatureFlagKey.SiloFillLevels
  | FeatureFlagKey.StoppedMainEquipment
  | FeatureFlagKey.KpiAggregationTypeOption
  | FeatureFlagKey.ChecklistsKilnCheckSheet
  | FeatureFlagKey.StoppageOverlapValidation
  | FeatureFlagKey.StoppageAnalysisGraphEditMode
  | FeatureFlagKey.AllowManualUpmCreation
  | FeatureFlagKey.Stock
  | FeatureFlagKey.PriceForecast
  | FeatureFlagKey.DemandForecast
  | FeatureFlagKey.Planning
  | FeatureFlagKey.LegacyElectricityPurchasing
  | FeatureFlagKey.ControlOperatorScreen
  | FeatureFlagKey.LegacyPlanningComments
  | FeatureFlagKey.LegacyOperatorViewExactElectricityPrices
  | FeatureFlagKey.LegacyOperatorViewColorCodedElectricityPrices
  | FeatureFlagKey.LegacyShutdownAvailability
  | FeatureFlagKey.LegacyCostAvoidance
  | FeatureFlagKey.UpmAssetsAndStorages
  | FeatureFlagKey.NewStandardOperationTimes
  | FeatureFlagKey.JanusShowPssTab
  | FeatureFlagKey.EnableOptimizedAssets
  | FeatureFlagKey.SuiteMenuCustomization
  | FeatureFlagKey.UseHproduceDomain
  | FeatureFlagKey.CostAvoidance
  | FeatureFlagKey.ElectricityPurchasing
  | FeatureFlagKey.PlanningComments
  | FeatureFlagKey.OperatorViewExactElectricityPrices
  | FeatureFlagKey.OperatorViewColorCodedElectricityPrices
  | FeatureFlagKey.ShutdownAvailability
  | FeatureFlagKey.RawMaterialsPlanning
  | FeatureFlagKey.ThreeShippingTypes
  | FeatureFlagKey.OptimizerStatusDisplay
  | FeatureFlagKey.OptimizerPlanAnalysis

export type FeatureFlagParams = {
  [FeatureFlagKey.HideWorkOrderNotification]: {plantId?: string}
  [FeatureFlagKey.ExtendedEquipmentIds]: {plantId?: string}
  [FeatureFlagKey.AutocompleteProcessStage]: {plantId?: string}
  [FeatureFlagKey.SubmitStoppage]: undefined
  [FeatureFlagKey.UseHproduceDomain]: undefined
  [FeatureFlagKey.SapMaintenanceNotification]: undefined
  [FeatureFlagKey.MovieAttachment]: undefined
  [FeatureFlagKey.LabQualityResultPage]: {plantId?: string}
  [FeatureFlagKey.MainEquipmentPrefill]: {plantId?: string}
  [FeatureFlagKey.MainEquipmentList]: {plantId?: string}
  [FeatureFlagKey.AllProcessStagesSummary]: {plantId?: string}
  [FeatureFlagKey.ProductionVolumes]: {plantId?: string}
  [FeatureFlagKey.ExpertSystem]: {plantId?: string}
  [FeatureFlagKey.SiloFillLevels]: {plantId?: string}
  [FeatureFlagKey.TermsAndConditions]: undefined
  [FeatureFlagKey.DailyReportingJanus]: undefined
  [FeatureFlagKey.InputRangesAndDecimals]: undefined
  [FeatureFlagKey.MonthlyReportingJanus]: undefined
  [FeatureFlagKey.PlantAdministration]: undefined
  [FeatureFlagKey.PSSJanus]: undefined
  [FeatureFlagKey.PSSStandardCapacities]: undefined
  [FeatureFlagKey.ReportingReminders]: undefined
  [FeatureFlagKey.SLATracking]: undefined
  [FeatureFlagKey.Validation]: undefined
  [FeatureFlagKey.ValidationAndChecks]: undefined
  [FeatureFlagKey.VersionManagement]: undefined
  [FeatureFlagKey.Checklists]: {plantId?: string}
  [FeatureFlagKey.FunctionalLocationOptional]: {plantId?: string}
  [FeatureFlagKey.InforMaintenanceNotification]: undefined
  [FeatureFlagKey.EnableSplitStoppage]: undefined
  [FeatureFlagKey.Notifications]: undefined
  [FeatureFlagKey.StoppedMainEquipment]: undefined
  [FeatureFlagKey.KpiAggregationTypeOption]: {plantId?: string}
  [FeatureFlagKey.ChecklistsKilnCheckSheet]: {plantId?: string}
  [FeatureFlagKey.StoppageOverlapValidation]: undefined
  [FeatureFlagKey.StoppageAnalysisGraphEditMode]: undefined
  [FeatureFlagKey.RiskSurvey]: undefined
  [FeatureFlagKey.AllowManualUpmCreation]: {plantId?: string}
  [FeatureFlagKey.Stock]: {plantId?: string}
  [FeatureFlagKey.PriceForecast]: {plantId?: string}
  [FeatureFlagKey.DemandForecast]: {plantId?: string}
  [FeatureFlagKey.Planning]: {plantId?: string}
  [FeatureFlagKey.LegacyElectricityPurchasing]: {plantId?: string}
  [FeatureFlagKey.ControlOperatorScreen]: {plantId?: string}
  [FeatureFlagKey.LegacyPlanningComments]: {plantId?: string}
  [FeatureFlagKey.LegacyOperatorViewExactElectricityPrices]: {plantId?: string}
  [FeatureFlagKey.LegacyOperatorViewColorCodedElectricityPrices]: {plantId?: string}
  [FeatureFlagKey.LegacyShutdownAvailability]: {plantId?: string}
  [FeatureFlagKey.LegacyCostAvoidance]: {plantId?: string}
  [FeatureFlagKey.UpmAssetsAndStorages]: {plantId?: string}
  [FeatureFlagKey.NewStandardOperationTimes]: {plantId?: string}
  [FeatureFlagKey.JanusShowPssTab]: {plantId?: string}
  [FeatureFlagKey.EnableOptimizedAssets]: {plantId?: string}
  [FeatureFlagKey.SuiteMenuCustomization]: undefined
  [FeatureFlagKey.ElectricityPriceForecast]: {plantId?: string}
  [FeatureFlagKey.SalesForecast]: {plantId?: string}
  [FeatureFlagKey.OperatorView]: {plantId?: string}
  [FeatureFlagKey.StorageLevels]: {plantId?: string}
  [FeatureFlagKey.ProductionPlanning]: {plantId?: string}
  [FeatureFlagKey.Optimizer]: {plantId?: string}
  [FeatureFlagKey.CostAvoidance]: {plantId?: string}
  [FeatureFlagKey.ElectricityPurchasing]: {plantId?: string}
  [FeatureFlagKey.PlanningComments]: {plantId?: string}
  [FeatureFlagKey.OperatorViewExactElectricityPrices]: {plantId?: string}
  [FeatureFlagKey.OperatorViewColorCodedElectricityPrices]: {plantId?: string}
  [FeatureFlagKey.ShutdownAvailability]: {plantId?: string}
  [FeatureFlagKey.RawMaterialsPlanning]: {plantId?: string}
  [FeatureFlagKey.ThreeShippingTypes]: {plantId?: string}
  [FeatureFlagKey.OptimizerStatusDisplay]: {plantId?: string}
  [FeatureFlagKey.OptimizerPlanAnalysis]: {plantId?: string}
}

export type AllowedKeys<C extends ConfigurationName> = C extends 'suite'
  ? SuiteFeatureFlags
  : HProduceCommonFeatureFlags
